/**
 * AuthContext — exposes the authenticated user + role helpers to the SPA.
 *
 * Two modes are supported:
 *
 * * **OIDC mode** (default) — drives the real Keycloak flow through
 *   ``oidc-client-ts``.  Requires the IdP to be reachable.  Each user
 *   carries a single dedicated role (login dedicado).
 * * **Demo mode** — opt-in via ``VITE_ANC_DEMO=1`` (or
 *   ``localStorage.setItem('anc.demo', '1')``).  Skips the IdP and lets
 *   the visitor pick which single role to impersonate, so the SPA can be
 *   explored from every persona without external services.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  getUser,
  login as oidcLogin,
  logout as oidcLogout,
  oidc,
  rolesFromUser,
  ALL_VALID_ROLES,
  type Role,
  type UserLike,
} from "./oidc";

interface AuthState {
  user: UserLike | null;
  roles: Role[];
  isLoading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  isDemo: boolean;
  /** Active (single) role driving the UI. */
  activeRole: Role | null;
  /** In demo mode, switch the impersonated role. */
  setDemoRole: (role: Role) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEMO_FLAG_LOCAL_STORAGE_KEY = "anc.demo";
const DEMO_ROLE_LOCAL_STORAGE_KEY = "anc.demo.role";

const DEFAULT_DEMO_ROLE: Role = "Gerente";

function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.VITE_ANC_DEMO === "1") return true;
  try {
    return window.localStorage.getItem(DEMO_FLAG_LOCAL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function readDemoRole(): Role {
  if (typeof window === "undefined") return DEFAULT_DEMO_ROLE;
  try {
    const raw = window.localStorage.getItem(DEMO_ROLE_LOCAL_STORAGE_KEY);
    if (raw && (ALL_VALID_ROLES as string[]).includes(raw)) {
      return raw as Role;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_DEMO_ROLE;
}

function demoUserForRole(role: Role): UserLike {
  // Login dedicado: the demo user carries exactly one role.
  return { profile: { roles: [role] } };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const demo = isDemoMode();
  const [state, setState] = useState<AuthState>(() => {
    if (demo) {
      const role = readDemoRole();
      const user = demoUserForRole(role);
      return {
        user,
        roles: rolesFromUser(user),
        isLoading: false,
        error: null,
      };
    }
    return { user: null, roles: [], isLoading: true, error: null };
  });

  const refresh = useCallback(async () => {
    if (demo) {
      const role = readDemoRole();
      const user = demoUserForRole(role);
      setState({
        user,
        roles: rolesFromUser(user),
        isLoading: false,
        error: null,
      });
      return;
    }
    try {
      const oidcUser = await getUser();
      // oidc-client's `User` carries IdTokenClaims, which structurally
      // is wider than our `UserLike`. Cast through unknown to avoid TS
      // narrowing the profile shape.
      const user = (oidcUser as unknown as UserLike | null) ?? null;
      setState({
        user,
        roles: rolesFromUser(user),
        isLoading: false,
        error: null,
      });
    } catch (err) {
      setState({
        user: null,
        roles: [],
        isLoading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, [demo]);

  useEffect(() => {
    void refresh();
    if (demo) {
      return undefined;
    }
    const onUserLoaded = () => {
      void refresh();
    };
    const onUserUnloaded = () => {
      setState({ user: null, roles: [], isLoading: false, error: null });
    };
    oidc.events.addUserLoaded(onUserLoaded);
    oidc.events.addUserUnloaded(onUserUnloaded);
    return () => {
      oidc.events.removeUserLoaded(onUserLoaded);
      oidc.events.removeUserUnloaded(onUserUnloaded);
    };
  }, [demo, refresh]);

  const setDemoRole = useCallback(
    (role: Role) => {
      if (!demo) return;
      try {
        window.localStorage.setItem(DEMO_ROLE_LOCAL_STORAGE_KEY, role);
      } catch {
        /* ignore */
      }
      const user = demoUserForRole(role);
      setState({
        user,
        roles: rolesFromUser(user),
        isLoading: false,
        error: null,
      });
    },
    [demo],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isDemo: demo,
      activeRole: state.roles[0] ?? null,
      setDemoRole,
      login: demo
        ? async () => {
            const role = readDemoRole();
            const user = demoUserForRole(role);
            setState({
              user,
              roles: rolesFromUser(user),
              isLoading: false,
              error: null,
            });
          }
        : oidcLogin,
      logout: demo
        ? async () => {
            try {
              window.localStorage.removeItem(DEMO_FLAG_LOCAL_STORAGE_KEY);
              window.localStorage.removeItem(DEMO_ROLE_LOCAL_STORAGE_KEY);
            } catch {
              /* ignore */
            }
            window.location.reload();
          }
        : oidcLogout,
      hasRole: (role) => state.roles.includes(role),
      hasAnyRole: (roles) => roles.some((r) => state.roles.includes(r)),
    }),
    [demo, state, setDemoRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}

export { ROLE_META, ALL_VALID_ROLES } from "./oidc";
export type { Role, UserLike } from "./oidc";
